import { useMemo } from "react";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
  PowerSettingsNew as PowerIcon,
  Inventory as InventoryIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Category as GroupIcon,
  Bolt as BoltIcon,
  OfflineBolt as OfflineBoltIcon,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import type { CasterTreeRow } from "../models";
import { isGroupType, isStrandContainer, PART_TYPE_IDS } from "../models";
import { StatusIndicator } from "../../../shared/components/StatusIndicator";

const cellStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

/** Pixels per tree level for indentation */
const TREE_INDENT_PX = 20;

interface Props {
  onSetOnline: (row: CasterTreeRow) => void;
  onSetOffline: (row: CasterTreeRow) => void;
}

export const useCasterTreeTableData = ({
  onSetOnline,
  onSetOffline,
}: Props) => {
  const theme = useTheme();

  const columnDefs: ColDef<CasterTreeRow>[] = useMemo(
    () => [
      {
        headerName: "",
        field: "EquipmentName",
        minWidth: 300,
        flex: 1,
        cellStyle: {
          ...cellStyle,
          justifyContent: "flex-start",
          paddingLeft: theme.spacing(1),
        },
        cellRenderer: (params: ICellRendererParams<CasterTreeRow>) => {
          if (!params.data) return null;

          const { rowType } = params.data;
          const node = params.node;
          const hasChildren =
            node.allChildrenCount && node.allChildrenCount > 0;
          const isExpanded = node.expanded;

          const level = node.level || 0;
          const indentPx = level * TREE_INDENT_PX;

          let displayName: string;
          let icon: React.ReactNode = null;
          let showStatus = false;
          let showGroupIcon = false;

          switch (rowType) {
            case "container":
              displayName = params.data.ContainerName || "";
              if (isStrandContainer(params.data.ContainerId)) {
                icon = params.data.HasMoldInstalled ? (
                  <BoltIcon fontSize="small" sx={{ color: "success.main" }} />
                ) : (
                  <OfflineBoltIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                );
              } else {
                const isOnlineContainer = displayName
                  .toLowerCase()
                  .includes("online");
                icon = isOnlineContainer ? (
                  <CheckCircleIcon
                    fontSize="small"
                    sx={{ color: "success.main" }}
                  />
                ) : (
                  <CancelIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                );
              }
              break;

            case "sparesContainer":
              displayName = params.data.ContainerName || "";
              icon = (
                <InventoryIcon
                  fontSize="small"
                  sx={{ color: "warning.main" }}
                />
              );
              break;

            case "equipment":
              displayName = params.data.EquipmentName || "";
              break;

            case "location":
              displayName = params.data.PositionName || "";
              // Group containers (Flanges, Collars, Seals, Foot Roll) show group icon
              showGroupIcon =
                isGroupType(params.data.PartTypeId) &&
                params.data.HasChildren === true;
              // Non-group locations with no children show status
              showStatus = !showGroupIcon && params.data.HasChildren !== true;
              break;

            case "sparePart":
              displayName =
                [
                  params.data.PartInfo?.SerialNumber,
                  params.data.PartInfo?.WarehouseNumber,
                  params.data.PartInfo?.DrawingNumber,
                ]
                  .filter(Boolean)
                  .join(" - ") || "-";
              showStatus = true;
              break;

            default:
              displayName = params.data.EquipmentName || "";
          }

          const statusCode = params.data.StatusCode;

          const handleExpandClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            node.setExpanded(!isExpanded);
            params.api.refreshCells({
              rowNodes: [node],
              columns: [params.column!],
              force: true,
            });
          };

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 0.5,
                width: "100%",
                height: "100%",
                pl: `${indentPx}px`,
              }}
            >
              {hasChildren ? (
                <IconButton
                  size="small"
                  onClick={handleExpandClick}
                  sx={{
                    p: 0,
                    color: "text.secondary",
                  }}
                >
                  {isExpanded ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </IconButton>
              ) : (
                <Box sx={{ width: 20 }} />
              )}
              {icon}
              {showStatus ? (
                <StatusIndicator statusCode={statusCode ?? "B"} />
              ) : null}
              {showGroupIcon ? (
                <Tooltip title="Component Group">
                  <GroupIcon
                    fontSize="small"
                    sx={{ color: "info.main", ml: 0.5 }}
                  />
                </Tooltip>
              ) : null}
              <span>{displayName}</span>
            </Box>
          );
        },
      },
      {
        field: "Heats",
        headerName: "Campaign",
        headerTooltip: "Heats since part was installed (Campaign)",
        width: 110,
        cellStyle: cellStyle,
        valueFormatter: (params) => {
          const rowType = params.data?.rowType;
          if (rowType === "container" || rowType === "sparesContainer")
            return "-";
          return params.value != null ? params.value.toLocaleString() : "-";
        },
      },
      {
        field: "HeatsSinceRepair",
        headerName: "Since Last Repair",
        headerTooltip: "Heats since last repair",
        width: 110,
        cellStyle: cellStyle,
        valueFormatter: (params) => {
          const rowType = params.data?.rowType;
          if (rowType === "container" || rowType === "sparesContainer")
            return "-";
          if (rowType === "equipment") return "-";
          if (rowType === "location" && !params.data?.HasAssignedPart)
            return "-";
          return params.value != null ? params.value.toLocaleString() : "-";
        },
      },
      {
        field: "TotalHeats",
        headerName: "Total",
        headerTooltip: "Total lifetime heats",
        width: 90,
        cellStyle: cellStyle,
        valueFormatter: (params) => {
          const rowType = params.data?.rowType;
          if (rowType === "container" || rowType === "sparesContainer")
            return "-";
          if (rowType === "location" && !params.data?.HasAssignedPart)
            return "-";
          return params.value?.toLocaleString() ?? "0";
        },
      },
      {
        field: "InstalledDate",
        headerName: "Last Action",
        width: 150,
        cellStyle: cellStyle,
        valueFormatter: (params) => {
          if (!params.value) return "-";
          return dxtToLocalServerTime(params.value, "MM/dd/yyyy HH:mm");
        },
      },
      {
        headerName: "Days",
        width: 80,
        cellStyle: cellStyle,
        valueGetter: (params) => {
          const rowType = params.data?.rowType;
          if (rowType === "container" || rowType === "sparesContainer")
            return "-";
          if (!params.data?.InstalledDate) return "-";
          if (params.data.Status === "Offline") return "-";

          const installedDate = new Date(params.data.InstalledDate);
          const currentDate = new Date();
          const diffTime = Math.abs(
            currentDate.getTime() - installedDate.getTime(),
          );
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          return diffDays.toString();
        },
      },
      {
        headerName: "",
        width: 60,
        cellStyle: cellStyle,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<CasterTreeRow>) => {
          const row = params.data;
          if (!row) return null;

          // Show power button for mold equipment rows
          const isMoldEquipment =
            row.rowType === "equipment" &&
            row.PartTypeId === PART_TYPE_IDS.MOLD;

          if (!isMoldEquipment) return null;

          const isOnline = row.Status === "Online";
          return (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {isOnline ? (
                <Tooltip title="Set Offline">
                  <IconButton
                    size="medium"
                    sx={{ color: "success.main" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetOffline(row);
                    }}
                  >
                    <PowerIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Set Online">
                  <IconButton
                    size="medium"
                    sx={{ color: "text.secondary" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetOnline(row);
                    }}
                  >
                    <PowerIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ],
    [onSetOnline, onSetOffline, theme],
  );

  const autoGroupColumnDef: ColDef<CasterTreeRow> = useMemo(
    () => ({
      headerName: "",
      width: 0,
      maxWidth: 0,
      minWidth: 0,
      resizable: false,
      sortable: false,
      suppressSizeToFit: true,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: () => null,
      },
    }),
    [],
  );

  return { columnDefs, autoGroupColumnDef };
};
